<template>
    <div v-if="canUpdate">
        <settings-heading
            :title="$t('settings.integrations.title')"
            :description="$t('settings.integrations.description')"
        />

        <ul class="mb-64">

            <multi-posting-connector v-if="multiposting" :locations="locations" :prelaunched="prelaunch.multiPosting" />

            <seek-connector v-if="seekEnabled" :prelaunched="prelaunch.seek" />

            <vdab-connector :locations="locations" :prelaunched="prelaunch.vdab" />

            <zapier-connector :prelaunched="prelaunch.zapier" />

            <div
                class="rounded-6 overflow-hidden bg-white border border-primary-20 transition-all"
                :class="prelaunch.officient ? 'my-16 pt-28 pl-28 pb-12 pr-12 shadow-lg bg-secondary-5' : 'mb-8 p-32 shadow-sm'"
            >
                <div class="flex items-center gap-24">
                    <business-avatar
                        icon="officient"
                        :link="officientUrl"
                        class="!bg-officient hover:!bg-officient-light text-white"
                    />
                    <div class="mr-auto">
                        <h3>
                            <span class="text-body first-letter:uppercase font-semibold">
                                {{ $t('settings.integrations.officient.title') }}
                            </span>
                            <span> ({{
                                    $t(currentCompany.officient_linked ? 'settings.integrations.officient.linked' : 'settings.integrations.officient.not_linked')
                                }})</span>
                        </h3>
                        <p class="text-body-sm">
                            {{ $t('settings.integrations.officient.description') }}
                        </p>
                    </div>

                    <div class="pl-24 h-full flex items-center">
                        <div class="flex justify-end">
                            <simple-button
                                v-if="currentCompany.officient_linked"
                                @click="confirmUnlink"
                                :loading="unlinking"
                                display="danger"
                            >
                                {{ $t('settings.integrations.officient.forget_button') }}
                            </simple-button>
                            <simple-button
                                display="primary"
                                :loading="linking"
                                look="primary"
                                v-else
                                @click="redirectToOfficientAuth"
                            >
                                {{ $t('settings.integrations.officient.link_button') }}
                            </simple-button>
                        </div>
                    </div>
                </div>
            </div>
        </ul>

        <popup-modal-confirm
                v-if="unlinking"
                :message="$t('settings.integrations.officient.unlink_modal_description')"
                :working="performingUnlinkingRequest"
                @close="cancelUnlink"
                @confirm="unlink"
            >
        </popup-modal-confirm>
    </div>
    <error-403 v-else></error-403>
</template>

<script>
import _debounce from 'lodash/debounce';
import SimpleButton from '../../components/ui/SimpleButton.vue';
import Input from '../../components/forms/Input.vue';
import { mapState, mapActions } from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import * as companies from '../../services/CompanyService';
import Company from '../../models/Company';
import { ADD_NOTIFICATION } from '../../state/mutation-types';
import { normalizeJson } from '../../../shared/helpers';
import Error403 from '../../components/ui/Error403.vue';
import * as locationService from "../../services/LocationService";
import Location from "../../models/Location";
import PopupModalConfirm from '../../components/ui/PopupModalConfirm.vue';
import VdabConnector from '../../components/settings/VdabConnector.vue';
import ZapierConnector from '../../components/settings/ZapierConnector.vue';
import SeekConnector from '../../components/settings/SeekConnector.vue';
import MultiPostingConnector from "../../components/settings/MultiPostingConnector.vue";
import Feature from "../../models/Feature";
import BusinessAvatar from "../../components/ui/BusinessAvatar.vue";
import SettingsHeading from "../../components/ui/SettingsHeading.vue";

export default {
    components: {
        SettingsHeading,
        BusinessAvatar,
        MultiPostingConnector,
        'simple-button': SimpleButton,
        'simple-input': Input,
        'error-403': Error403,
        'popup-modal-confirm': PopupModalConfirm,
        'vdab-connector': VdabConnector,
        'zapier-connector': ZapierConnector,
        'seek-connector': SeekConnector,
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        locations () {
            return useRepo(Location).all();
        },
        multiposting() {
            return this.showFeature('multiposting');
        },
        seekEnabled() {
            return this.showFeature('seek');
        },
        currentOAuthState() {
            return sessionStorage.getItem(this.oauthStateKey);
        },
        oauthStateKey() {
            return "oauth_state"
        }
    },
    data: function () {
        return {
            canUpdate: this.$can('settings.advanced'),
            linking: false,
            unlinking: false,
            performingUnlinkingRequest: false,
            officientUrl: Jobtoolz.config.officient.app_url,
            prelaunch: {
                multiPosting: false,
                officient: false,
                vdab: false,
                zapier: false,
                seek: false,
            },
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        createOAuthState() {
            const state = window.crypto.randomUUID();
            sessionStorage.setItem(this.oauthStateKey, state);
            return state;
        },
        clearOAuthState() {
            sessionStorage.removeItem(this.oauthStateKey);
        },
        showFeature(key) {
            const feature = useRepo(Feature)
                .where('key', key)
                .first();
            return feature && this.currentCompany.feature_ids.includes(feature.id);
        },
        setOfficientBtnOpacity (value) {
            this.officientBtnOpacity = value;
        },
        redirectToOfficientAuth () {
            this.linking = true;
            location.replace(Jobtoolz.config.officient.auth_url + '?client_id=' + Jobtoolz.config.officient.client_id +
                '&state=' + this.createOAuthState());
        },
        confirmUnlink () {
            this.unlinking = true;
        },
        cancelUnlink () {
            this.unlinking = false;
        },
        async unlink () {
            this.performingUnlinkingRequest = true;

            await companies.update(this.currentCompany.id, {
                data: {
                    attributes: {
                        officient_linked: false
                    }
                }
            }).then(result => {
                return useRepo(Company)
                    .whereId(result.id)
                    .update(normalizeJson(result));
            }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('settings.saved', {subject: this.$t('settings.company.title')}),
                });
            });

            this.performingUnlinkingRequest = false;
            this.unlinking = false;
        },
        async link (code) {
            this.linking = true;

            await companies.linkOfficient(this.currentCompany.id, {
                data: {
                    attributes: {
                        authcode: code
                    }
                }
            }).then((result) => {
                return companies.show(this.currentCompany.id);
            }).then(result => {
                return useRepo(Company)
                    .whereId(result.id)
                    .update(normalizeJson(result.data));
            }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('settings.saved', {subject: this.$t('settings.company.title')}),
                });
            }).finally(() => {
                this.linking = false;
                this.$router.replace({});
            });
        },
        preLaunchIntegration () {
            let integration = this.$route.name.replace('settings.integrations.', '');

            if (integration) {
                setTimeout(() => { this.prelaunch[integration] = true }, 1000);
            }
        }
    },
    created () {
        locationService.index().then(result => useRepo(Location).save(normalizeJson(result.data)));
    },
    mounted () {
        if (this.$route.query.code &&
            this.$route.query.state &&
            this.$route.query.state === this.currentOAuthState) {

            this.link(this.$route.query.code);
        }
        this.clearOAuthState();

        if (this.$route.name !== 'settings.integrations') {
            this.preLaunchIntegration();
        }
    }
}
</script>

<style scoped>
.transition-all {
  transition: all .5s ease-in-out;
}
</style>
